import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Product } from '@app/modules/model/product';
import { ProductSelect } from '@app/modules/model/productselect';
import { Ticket } from '@app/modules/model/ticket';

import { languageSupport, languageService } from '@app/modules/common/language';
import { alertService } from '@app/modules/common/alert';
import { viewService } from '@app/modules/view';

import { GenericUtils } from '@app/app.commonutils';

@Component({
    selector: 'ui-product-thumbnail',
    templateUrl:'./ui-thumb.html',
    styleUrls: [ 
        './ui-thumb.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiProductThumbnail extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('product') _product: Product = null;
    @Input('direct') _direct: ProductSelect = null;
    @Input('active') _active = true;
    @Input('ticket') _ticket: Ticket = null;
    @Input('target') _target = null;
    @Input('checked') _checked = false;

    private _subscriptions = [];

    get thumbtype(){
        if (this._direct != null){
            return "direct";
        }

        if (this._product != null){
            if (this._product.isgroup){
                return "group"
            }
            else {
                return "product";
            }
        }
        
        return "unknown"
    }

    get thumbname(){
        return (this.thumbtype == 'direct') ? this._direct.name : this._product.name;
    }

    normalize(name){
        return GenericUtils.Normalize(name);
    }

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    private _isProductPurchased(_product){
        if (_product.isgroup){
            return false;   // do not count groups
        }

        if (!_product.IsPurchased){
            return false;   // is not purchased
        }

        if (!_product.IsChild(this._product)){
            return false;   // is not child of this product
        }

        if ((this.view.Access == 'GUEST') && (_product.HideApp)) {
            return false;   // not suitable for this access
        }

        if ((this.view.Access == 'DEVRY') && (_product.HideWeb)) {
            return false;   // not suitable for this access
        }

        return true;
    }

    private _isFamilyItemPurchased(_fmproduct){
        if (_fmproduct.product && _fmproduct.product.IsPurchased){
            return true;
        }

        if (_fmproduct.select && _fmproduct.select.IsAvailable){
            return true;
        }

        return false;
    }

    private _purchased = null;
    get Purchased(){
        if (this._purchased === null){
            let _purchased = 0;

            // check the purchased items for families
            if (this._product.IsFamily){
                for (let _familyproduct of this._product.family.items){
                    if (this._isFamilyItemPurchased(_familyproduct)){
                        _purchased++;
                    }
                }
            }

            // check the purchased items for groups
            else {
                for(let _product of this._product.place.products){
                    if (this._isProductPurchased(_product)){
                        _purchased++;
                    }
                }    
            }

            this._purchased = _purchased;
        }

        return this._purchased;
    }

    private _incart = null;
    get inCart(){
        if ((!this._product) || (!this._ticket)){
            return [];
        }

        if (!this._incart) {
            let _incart = [];
            
            if (this._product.isgroup){
                for(let _ticketproduct of this._ticket.products){
                    if (!['DE', 'SP'].includes(_ticketproduct.status) && (_ticketproduct.product.IsChild(this._product))){
                        _incart.push(_ticketproduct);
                    }
                }
            }
            else {            
                for(let _ticketproduct of this._ticket.products){
                    if (['DE', 'SP'].includes(_ticketproduct.status)){
                        continue;
                    }

                    if (_ticketproduct.product == this._product){
                        if (this._target == 'direct'){
                            if (this._direct){  // direct provided: add value in this thumbnail
                                if (this._direct.IsChosen(_ticketproduct)){
                                    _incart.push(_ticketproduct);
                                }    
                            }
                            else {  // direct not provided: add value if not in any existing direct
                                let _hasdirect = (_ticketproduct.options.length == 0);
                                if (!_hasdirect){
                                    for(let _direct of this._product.selects){
                                        if (_direct.IsChosen(_ticketproduct)){
                                            _hasdirect = true;
                                        }
                                    }    
                                }
    
                                if (!_hasdirect){   // cannot add to an existing direct: add here
                                    _incart.push(_ticketproduct);
                                }
                            }
                        }
                        else {
                            _incart.push(_ticketproduct);
                        }
                    }
                }
            }

            this._incart = _incart;
        }


        return this._incart || [];
    }

    get CheckIcon(){
        return (this._checked) ? 'checkmark-circle-outline' : 'radio-button-off-outline';
    }

    /***********************************/
    /* PRODUCT DATA                    */
    /***********************************/

    private _data = {
        updated: false,
        catalog: null,
        available: null,
        purchased: null,
        starting: null,
        price: null,
        marketprice: null
    }

    private _reloadProduct(){
        this._data.catalog = this._product ? this._product.IsCatalog : false;
        this._data.available = this._product ? this._product.IsAvailable : false;
        this._data.purchased = this._product ? this._product.IsPurchased : false;
        this._data.starting = this._product ? this._product.Starting : null;

        this._data.marketprice = this._product ? this._product.MarketPrice : false;

        this._data.price = null;
        if (this._product && this._product.price != null && !this._product.IsGeneric && !this._product.IsVarious){
            this._data.price = this._product.Price;
        }
        
        if (this._direct && !(this._product && (this._product.IsGeneric || this._product.IsVarious))){
            this._data.price = this._direct.Price;
        }

        this.change.markForCheck();  
        this._data.updated = true;
    }

    private _reloadTicket(){
        this._incart = null;        
        this.change.markForCheck();
    }

    get IsCatalog(){
        if (!this._data.updated){
            this._reloadProduct();
        }
        return this._data.catalog;
    }

    get IsAvailable(){
        if (!this._data.updated){
            this._reloadProduct();
        }
        return this._data.available;
    }

    get IsPurchased(){
        if (!this._data.updated){
            this._reloadProduct();
        }
        return this._data.purchased;
    }

    get MarketPrice(){
        if (!this._data.updated){
            this._reloadProduct();
        }
        return this._data.marketprice;
    }    

    get Starting(){
        if (!this._data.updated){
            this._reloadProduct();
        }
        return this._data.starting;
    }    

    get Price(){
        if (!this._data.updated){
            this._reloadProduct();
        }
        return this._data.price;
    }    

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, public view: viewService) {
        super(lang, change);
    }

    private _theme_subscription = null;
    ngOnInit(){
        this._theme_subscription = this.view.OnTheme.subscribe(
        data => {
            this.change.markForCheck();
        });
    }

    ngOnChanges(){
        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
        
        if (this._active){  // only register to changes if thumbnail is active
            if (this._product){
                this._reloadProduct();

                this._subscriptions.push(
                    this._product.OnRefresh.subscribe(
                    data => {
                        this._reloadProduct();
                    })
                );    
                
                if (this._product.isgroup){
                    this._product.place.OnTargetRefresh('PRODUCT').subscribe(
                        data => {
                            this._purchased = null;
                            this.change.markForCheck();
                        });        
                }
            }
    
            if (this._ticket){
                this._reloadTicket();

                this._subscriptions.push(
                    this._ticket.OnRefresh.subscribe(
                    data => {
                        this._reloadTicket();
                    })
                );
            }
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._theme_subscription){
            this._theme_subscription.unsubscribe();
            this._theme_subscription = null;
        }

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    get CanShowAmountButton(){
        if (!this._product){
            return false;
        }

        // Guest mode = client ordering from QR. The amount shortcut is a
        // POS-only convenience: we don't want a customer to add 250
        // pizzas in one tap.
        if (this.view.Access == 'GUEST'){
            return false;
        }

        // Do not show on products with market price or weighted products
        if (this._product.MarketPrice || this._product.IsWeighted){
            return false;
        }

        // What the click will emit (mirrors OnAmountClick):
        //   target=direct + _direct → _direct (ProductSelect, options resolved)
        //   target=direct + !_direct → _product ("create direct" card)
        //   target=ticket            → _product
        //
        // HasSelects must skip the amount shortcut when the emitted item
        // is the base product (option-picking would be bypassed). A
        // _direct already carries its preselected options, so it's safe.
        let _emitsBaseProduct = !((this._target == 'direct') && this._direct);
        if (_emitsBaseProduct && this._product.HasSelects){
            return false;
        }

        if (this._target == 'direct'){
            // Only show for resolved directs. The special "create direct"
            // card (no _direct) must continue through the normal flow.
            return this._direct != null;
        }

        if (this._target == 'ticket' && !this._product.isgroup){
            return true;
        }

        return false;
    }

    @Output('onPressed') _onPressed = new EventEmitter<any>();
    async OnAmountClick(event){
        event.stopPropagation();

        if (!this.CanShowAmountButton){
            return;
        }

        this._onPressed.emit(((this._target == 'direct') && this._direct) ? this._direct : this._product);
    }

    private _OnRemove(){
        for(let _product of this.inCart){
            if (_product.product == this._product){
                this._ticket.CartDel(_product);
            }
        }
    }

    async OnRemove(event){
        event.stopPropagation();

        // Solo confirma si hay mas de uno; con un solo item lo eliminamos directo.
        if (this.inCart.length > 1){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@ticket_remove_products_confirm_tittle'),
                message: this.lang.tr('@ticket_remove_products_confirm_message', [ this._product.name, this.inCart.length ]),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => { alert.dismiss(true); }, 100);
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => { alert.dismiss(false); }, 100);
                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail: any) => {
                if (detail && detail.data){
                    this._OnRemove();
                }
            });

            await alert.present();
        }
        else {
            this._OnRemove();
        }
    }

    @Output('onSelect') _onSelect = new EventEmitter<any>();
    async OnSelect(){
        this._onSelect.emit(((['direct', 'select'].includes(this._target)) && this._direct) ? this._direct : this._product);
    }

    @Output('onEdit') _onEdit = new EventEmitter<any>();   
    async OnEdit(event){
        event.stopPropagation();
        this._onEdit.emit(this._product);
    }

    @Output('onCheck') _onCheck = new EventEmitter<any>();   
    async OnCheck(event){
        event.stopPropagation();
        if (this._target == 'select'){
            this._onCheck.emit(this._direct || this._product);
        }
    }

}
